// Handling of "service" messages: messages which are sent over the wire as ordinary
// `{pub}` packets but which must never be shown in the timeline as a chat bubble.
//
// The mobile clients (see ios/Tinodios/ReactionHelper.swift and
// ios/Tinodios/MessageViewController+MessageCellDelegate.swift) use two such messages:
//
//  1. Emoji reactions on somebody else's message:
//       head = {type: "reaction", targetSeq: <seq>, emoji: "👍", silent: true}
//       content = " "
//     The receiver is expected to fold the reaction into the target message.
//
//  2. Emoji reactions on one's own message: the author re-publishes the message with
//       head = {replace: ":<seq>", reactions: {"👍": ["usrA"]}, reactionOnly: true}
//     i.e. the reaction state rides on the message itself.
//
//  3. Note-sync notifications:
//       head = {type: "note"|"note_delete"}
//       content = "📝 Note updated: <title>"
//
//  4. A request for the full-quality original of a photo:
//       head = {type: "hdreq", targetSeq: <seq>, silent: true}
//       content = " "
//     Sent by the RECIPIENT of a compressed photo. The sender's device answers by
//     re-publishing the target message with `replace` and either the original
//     attached (hd.s == "ready") or hd.s == "gone" if it cannot supply it.
//
// Without the filtering below reactions show up as empty bubbles and note syncs spam
// the timeline. iOS filters them in MessageViewController+MessageDisplayLogic.swift:172.

export const REACTION_HEAD_TYPE = 'reaction';
export const NOTE_HEAD_TYPES = ['note', 'note_delete'];
export const HD_REQUEST_HEAD_TYPE = 'hdreq';

// Emoji offered by the quick reaction picker. Same set as the iOS EmojiPickerView.
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

// True if the message carries a reaction and must not be rendered as a bubble.
export function isReactionMessage(head) {
  if (!head) {
    return false;
  }
  if (head.type == REACTION_HEAD_TYPE) {
    return true;
  }
  // Defensive: older/edge payloads which carry the reaction fields without the type.
  if (head.emoji && (head.targetSeq !== undefined || head.silent)) {
    return true;
  }
  return false;
}

// True if the message is a note-sync notification.
export function isNoteMessage(head) {
  return !!head && NOTE_HEAD_TYPES.includes(head.type);
}

// True if the message is a request for a photo's full-quality original.
export function isHDRequestMessage(head) {
  return !!head && head.type == HD_REQUEST_HEAD_TYPE;
}

// Seq of the photo an hdreq refers to, or 0.
export function hdRequestTargetSeq(head) {
  if (!isHDRequestMessage(head)) {
    return 0;
  }
  const seq = parseInt(head.targetSeq);
  return isNaN(seq) || seq <= 0 ? 0 : seq;
}

// True if the message must be hidden from the timeline altogether.
export function isServiceMessage(head) {
  return isReactionMessage(head) || isNoteMessage(head) || isHDRequestMessage(head);
}

// True if `head.replace` is present only because the author was toggling a reaction on
// their own message. Such messages must not be marked as "edited".
export function isReactionOnlyEdit(head) {
  return !!head && !!head.replace && !!head.reactionOnly;
}

// True if `head.replace` is present only because the sender's device attached (or
// declined to attach) the full-quality original. That is system enrichment, not
// something the author retyped, so it must not be marked as "edited" either.
// iOS already suppresses it in MessageViewController.editedMarkerText.
export function isHDOnlyEdit(head) {
  return !!head && !!head.replace && !!head.hdOnly;
}

// Self-destruct timer in seconds, 0 if the message is not timed.
export function getTimer(head) {
  if (!head) {
    return 0;
  }
  const t = parseInt(head.timer);
  return isNaN(t) || t <= 0 ? 0 : t;
}

// Normalize head.reactions ({emoji: [uid, ...]}) into a plain object, dropping junk.
function normalizeReactions(raw) {
  if (!raw || typeof raw != 'object' || Array.isArray(raw)) {
    return null;
  }
  const out = {};
  for (const emoji in raw) {
    const users = raw[emoji];
    if (Array.isArray(users) && users.length > 0) {
      out[emoji] = users.filter(u => typeof u == 'string');
      if (out[emoji].length == 0) {
        delete out[emoji];
      }
    }
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Walk the loaded messages of a topic and compute, for every message, the set of
 * reactions attached to it.
 *
 * Two sources are merged:
 *  - `head.reactions` on the message itself (written by the author via `head.replace`);
 *  - standalone `type: "reaction"` messages sent by everyone else.
 *
 * A standalone reaction message is a *toggle*, so an even number of them from the same
 * user for the same emoji cancels out. When a user has sent standalone reactions for a
 * given emoji, those win over whatever `head.reactions` claims about that user: the
 * author's copy of `head.reactions` is a snapshot of their local DB and may lag or
 * double-count reactions which also arrived as standalone messages.
 *
 * @param {Topic} topic - the topic to scan.
 * @returns {Object} map of seq -> {emoji: [uid, ...]}.
 */
export function collectReactions(topic) {
  // seq -> {emoji: [uid]} taken from head.reactions.
  const base = {};
  // seq -> {emoji: {uid: count}} taken from standalone reaction messages.
  const toggles = {};

  topic.messages(msg => {
    const head = msg.head;
    if (!head) {
      return;
    }

    if (isReactionMessage(head)) {
      const target = parseInt(head.targetSeq);
      const emoji = head.emoji;
      const from = msg.from;
      if (!target || isNaN(target) || !emoji || !from) {
        return;
      }
      const perMsg = toggles[target] = toggles[target] || {};
      const perEmoji = perMsg[emoji] = perMsg[emoji] || {};
      perEmoji[from] = (perEmoji[from] || 0) + 1;
      return;
    }

    const own = normalizeReactions(head.reactions);
    if (own) {
      base[msg.seq] = own;
    }
  });

  const result = {};
  const seqs = new Set(Object.keys(base).concat(Object.keys(toggles)));
  seqs.forEach(key => {
    const seq = parseInt(key);
    const merged = {};

    // Start from the author-provided snapshot, minus the users whose reactions we are
    // replaying from standalone messages (to avoid double-counting).
    const snapshot = base[seq] || {};
    for (const emoji in snapshot) {
      const replayed = (toggles[seq] || {})[emoji] || {};
      const users = snapshot[emoji].filter(uid => !(uid in replayed));
      if (users.length > 0) {
        merged[emoji] = users;
      }
    }

    // Apply the standalone reactions: odd count == reaction is on.
    const perMsg = toggles[seq] || {};
    for (const emoji in perMsg) {
      const users = [];
      for (const uid in perMsg[emoji]) {
        if (perMsg[emoji][uid] % 2 == 1) {
          users.push(uid);
        }
      }
      if (users.length > 0) {
        merged[emoji] = (merged[emoji] || []).concat(users);
      }
    }

    if (Object.keys(merged).length > 0) {
      result[seq] = merged;
    }
  });

  return result;
}
